"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AccordionMenuItem = void 0;
var _icons = require("@ori-ui/icons");
var _mui = require("@ori-ui/mui");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _react = require("react");
var _isCurrentLocation = require("../../utils/isCurrentLocation");
var _Icon = require("./Icon");
var _jsxRuntime = require("react/jsx-runtime");
const SubList = (0, _mui.styled)('ul', {
  shouldForwardProp: prop => prop !== 'isOpen'
})(({
  theme,
  isOpen = false
}) => ({
  maxHeight: 0,
  margin: 0,
  padding: 0,
  overflow: 'hidden',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'cubic-bezier(0, 1, 0.5, 1)',
  background: theme.palette.background.paper,
  listStyle: 'none',
  ...(isOpen && {
    maxHeight: theme.spacing(500),
    transitionDuration: '0.4s',
    transitionTimingFunction: 'ease-in'
  })
}));
const CustomLinkItem = (0, _mui.styled)('a', {
  shouldForwardProp: prop => prop !== 'hasIcon' && prop !== 'isActive'
})(({
  theme,
  hasIcon = false,
  isActive = false
}) => ({
  display: 'block',
  position: 'relative',
  boxSizing: 'border-box',
  padding: theme.spacing(2, 3, 2, 0),
  overflow: 'hidden',
  color: theme.palette.grey[700],
  backgroundColor: 'transparent',
  fontSize: '15px',
  letterSpacing: '0.5px',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  ...(hasIcon ? {
    lineHeight: theme.spacing(6),
    fontWeight: 'bold',
    '&:hover': {
      color: theme.palette.grey[700],
      backgroundColor: theme.palette.grey[100]
    },
    ...(isActive && {
      backgroundColor: theme.palette.grey[100]
    })
  } : {
    lineHeight: theme.spacing(4),
    '&:hover': {
      color: theme.palette.grey[400]
    },
    ...(isActive && {
      fontWeight: 'bold'
    })
  })
}));
const CustomButton = (0, _mui.styled)('button', {
  shouldForwardProp: prop => prop !== 'isOpen' && prop !== 'isClicked' && prop !== 'hasIcon'
})(({
  theme,
  hasIcon = false,
  isOpen = false,
  isClicked = false
}) => ({
  display: 'flex',
  position: 'relative',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  padding: theme.spacing(0, 2, 0, 0),
  color: theme.palette.grey[700],
  backgroundColor: 'transparent',
  fontSize: '15px',
  letterSpacing: '0.5px',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  ...(hasIcon ? {
    fontWeight: 'bold',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- this is the syntax
    ':hover': {
      backgroundColor: theme.palette.grey[100],
      cursor: 'pointer'
    }
  } : {
    '&:hover': {
      color: theme.palette.grey[400]
    }
  }),
  ...(isOpen && !isClicked && {
    fontWeight: 'bold'
  }),
  ...(isOpen && !hasIcon && {
    color: theme.palette.grey[700]
  })
}));
const Item = (0, _mui.styled)('li')(({
  theme
}) => ({
  borderBottom: `2px solid ${theme.palette.background.paper}`,
  listStyle: 'none',
  padding: 0,
  '& ul': {
    '& li': {
      marginLeft: theme.spacing(3)
    }
  }
}));
const SubItem = (0, _mui.styled)(Item)({
  borderBottom: 'initial'
});
const Text = (0, _mui.styled)('span')({
  textOverflow: 'ellipsis',
  overflow: 'hidden'
});
const Caret = (0, _mui.styled)(_icons.ArrowRightThin, {
  shouldForwardProp: prop => prop !== 'isOpen' && prop !== 'isSubmenu'
})(({
  isOpen = false,
  isSubmenu = false
}) => ({
  transition: 'transform ease-in 0.3s',
  transform: `rotate(${isOpen ? 270 : 90}deg)`,
  fontSize: isSubmenu ? '1.8rem' : '2.4rem'
}));
const AccordionMenuItem = ({
  menuItem,
  menuClickedItem,
  onMenuClick,
  isSubmenu = false
}) => {
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const isOpen = (0, _react.useMemo)(() => {
    if (menuClickedItem.path === menuItem.itemPath) {
      return menuClickedItem.open;
    }
    return menuClickedItem.path.startsWith(menuItem.itemPath);
  }, [menuClickedItem, menuItem]);
  const handleItemClick = event => onMenuClick(event, {
    path: menuItem.itemPath,
    open: !isOpen
  });
  let itemContent = null;
  let subItems = null;
  const ItemComponent = isSubmenu ? SubItem : Item;
  if (menuItem.subItems.length > 0) {
    itemContent = /*#__PURE__*/(0, _jsxRuntime.jsxs)(CustomButton, {
      isOpen: menuClickedItem.open,
      isClicked: !isOpen,
      hasIcon: Boolean(menuItem.icon),
      onClick: handleItemClick,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)(_mui.Stack, {
        direction: "row",
        overflow: "hidden",
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.Icon, {
          icon: menuItem.icon ?? null
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Text, {
          children: menuItem.title
        })]
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Caret, {
        isOpen: isOpen,
        isSubmenu: isSubmenu
      })]
    });
    subItems = /*#__PURE__*/(0, _jsxRuntime.jsx)(SubList, {
      isOpen: isOpen,
      children: menuItem.subItems.map(subitem => /*#__PURE__*/(0, _jsxRuntime.jsx)(AccordionMenuItem, {
        isSubmenu: true,
        menuItem: subitem,
        menuClickedItem: menuClickedItem,
        onMenuClick: onMenuClick
      }, subitem.itemPath))
    });
  } else {
    itemContent = /*#__PURE__*/(0, _jsxRuntime.jsx)(CustomLinkItem, {
      href: menuItem.url ?? '',
      target: menuItem.openInNewTab ? '_blank' : '_self',
      hasIcon: Boolean(menuItem.icon),
      isActive: (0, _isCurrentLocation.isCurrentLocation)(menuItem.url),
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_mui.Stack, {
        direction: "row",
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.Icon, {
          icon: menuItem.icon ?? null
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Text, {
          children: menuItem.title
        })]
      })
    });
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(ItemComponent, {
    "data-testid": getTestId(`accordion-${menuItem.itemPath.replaceAll('/', '')}`),
    children: [itemContent, subItems]
  });
};
exports.AccordionMenuItem = AccordionMenuItem;